import { useRequest } from 'ahooks';
import { Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ILegend } from 'bizcharts/lib/components/Legend';
import { IChartProps } from 'bizcharts/lib/interface';
import classnames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatNumberValueToChineseUnit } from '@/utils/format';
import type { ConBrandWebV1PotentialResponse, FinanceForBrandDetailDO } from '@/services/swagger/data-contracts';
import { ConBrandWebController } from '@/services/swagger/ConBrandWebController';
import { RootState } from '@/models';
import TooltipBase from '@/components/tooltip';
import Dot from '@/components/Dot';
import LineAndBarMixedChart from '@/components/charts/LineAndBarMixedChart';
import CustomEmpty from '@/business-components/CustomEmpty';
import Chart from '../chart';
import ChartHeader from '../chart-header';
import LockedCard, { LockCardState } from '../LockedCard';
import styles from './index.module.scss';

const controller = new ConBrandWebController();

const { Text } = Typography;

export type RacetrackAndBrandPotentialProps = {
  /** 品牌id */
  conBrandId: string;
  /** 品牌解锁状态 */
  brandLockState?: string;
  /** 融资情况 */
  financingInfo?: ConBrandWebV1PotentialResponse;
  /** 融资历程表 */
  finanncingList?: FinanceForBrandDetailDO[];
  /** 品牌解锁状态改变时回调 */
  onLockedCardStateChange?: (state: string) => void;
};

const scale: IChartProps['scale'] = {
  countTimeStr: {
    type: 'cat',
    alias: '月份',
  },
  growRate: {
    type: 'linear',
    min: 0,
    max: 100,
    tickCount: 2,
    nice: true,
    alias: '增长率',
    formatter(value) {
      return `${value}%`;
    },
  },
  value: {
    type: 'linear',
    nice: true,
    alias: '数量',
  },
};
const legendProps: ILegend = {
  name: 'dataTypeName',
  padding: [8, 0, 19, 0],
  // marker: {
  //   symbol: (x, y) => {
  //     const r = 2;
  //     return [
  //       ['M', x - 4 * r, y],
  //       ['L', x + 4 * r, y],
  //       ['M', x - r, y],
  //       ['A', r, r, 0, 0, 0, x + r, y],
  //       ['A', r, r, 0, 0, 0, x - r, y],
  //     ];
  //   },
  //   style(style) {
  //     const { stroke } = style;
  //     return { ...style, fill: stroke };
  //   },
  // },
  itemName: {
    style: {
      fill: '#999',
    },
  },
};

const cloutLegendProps: ILegend = {
  name: 'dataTypeName',
  maxWidth: 400,
  padding: [20, 0, 20, 0],
  flipPage: false,
  // marker: {
  //   symbol: (x, y) => {
  //     const r = 2;
  //     return [
  //       ['M', x - 4 * r, y],
  //       ['L', x + 4 * r, y],
  //       ['M', x - r, y],
  //       ['A', r, r, 0, 0, 0, x + r, y],
  //       ['A', r, r, 0, 0, 0, x - r, y],
  //     ];
  //   },
  //   style(style) {
  //     const { stroke } = style;
  //     return { ...style, fill: stroke };
  //   },
  // },
  itemName: {
    style: {
      fill: '#999',
    },
  },
};

const colors = ['#f75c2f', '#ffc2b0', '#666666', '#b8b0ad'];

const pageSize = 4;
const RacetrackAndBrandPotential: React.FC<RacetrackAndBrandPotentialProps> = ({
  conBrandId,
  financingInfo,
  finanncingList = [],
  brandLockState = 'not_member',
  onLockedCardStateChange,
}) => {
  const { token } = useSelector((state: RootState) => state.user);
  const onStateChange = useCallback(
    (state: string) => {
      onLockedCardStateChange?.(state);
    },
    [onLockedCardStateChange]
  );
  /** 经营能力：行业消费规模、行业讨论热度 */
  const { data: potential } = useRequest(
    async () => {
      const res = await controller.conBrandWebControllerV1TrackBrandPotentialUsingGet1({
        conBrandId,
      });
      const hotTalkEmpty =
        !res.data.data?.hotTalkList?.length || res.data.data?.hotTalkList?.every(item => !item?.value);
      return { ...res.data.data, hotTalkEmpty };
    },
    {
      refreshDeps: [brandLockState],
    }
  );
  const financingListColumns: ColumnsType<FinanceForBrandDetailDO> = [
    { title: '时间', dataIndex: 'financeTime', width: 120 },
    { title: '轮次', dataIndex: 'financeRoundsName', width: 100 },
    {
      title: '金额',
      dataIndex: 'totalFinancePriceStr',
      // width: 160,
      render(value) {
        return value ? `${value}` : '-';
      },
    },
    {
      title: '投资机构',
      dataIndex: 'financeChNameListStr',
      align: 'right',
      render(value) {
        const txt = value.split(',').join('、');
        return (
          <Text style={{ width: 260 }} ellipsis={value.length * 14 > 250 ? { tooltip: txt } : false}>
            {txt}
          </Text>
        );
      },
    },
  ];
  //品牌融资金额
  const totalFinanceAmount = useMemo(() => {
    if (!isNaN(+financingInfo?.totalFinanceAmount)) {
      return formatNumberValueToChineseUnit(+financingInfo?.totalFinanceAmount, 2);
    }
  }, [financingInfo?.totalFinanceAmount]);
  //行业融资总金额
  // const financeAmountNum = useMemo(() => {
  //   if (!isNaN(+financingInfo?.financeAmountNum)) {
  //     return formatNumberValueToChineseUnit(+financingInfo?.financeAmountNum * 1e4, 2);
  //   }
  // }, [financingInfo?.financeAmountNum]);
  return (
    !!token && (
      <>
        <ChartHeader
          title={
            <h1 className={styles.moduleTitle}>
              行业及品牌发展潜力
              <TooltipBase
                title={
                  <>
                    <p>行业发展潜力：由行业消费规模、行业融资环境、行业讨论热度等维度搭建的数学模型计算得出。</p>
                    <p>行业及品牌发展潜力数据来源：CBNData数据库、天眼查</p>
                  </>
                }
                placement="right"
              />
            </h1>
          }
          titleFontSize={18}
          className={styles.pontentialChartHeader}
        />
        <div className="flex-row-nowrap flex-between-start" style={{ marginTop: 20 }}>
          <Chart
            // title={
            //   <h2 className={styles.chartTitle}>
            //     融资情况
            //     <TooltipBase
            //       title="由组织能力、经营能力、行业及品牌潜力、产品竞争能力、供应链管理能力、用户发展能力、营销能力等七大指标维度计算得出，综合评价品牌的增长潜力"
            //       placement="right"
            //     />
            //   </h2>
            // }
            title="母公司融资情况"
            panelClassName={styles.financingInfoChart}
          >
            <div
              className={classnames(
                'flex-column-wrap flex-between-stretch',
                styles.dataInfoCard,
                styles.financingInfoCard
              )}
            >
              <div className="">
                <div className="flex-row-wrap flex-start-center">
                  <Dot />
                  <span className={styles.cardInfoName}>母公司累计融资次数</span>
                </div>
                <div className="flex-row-wrap flex-start-center">
                  {!financingInfo?.totalFinanceAmount ? (
                    <span className={styles.emptyStateText}>暂无数据</span>
                  ) : (
                    <>
                      <span className={styles.cardInfoValue}>{financingInfo?.totalFinanceNum}</span>
                      <span>次</span>
                    </>
                  )}
                </div>
              </div>
              {/*<div className="flex-row-nowrap flex-between-center">*/}
              {/*  <div className="flex-row-nowrap flex-between-center">*/}
              {/*    <Dot />*/}
              {/*    <span className={styles.cardInfoName}>融资总次数</span>*/}
              {/*  </div>*/}
              {/*  <div className="flex-row-nowrap flex-between-center">*/}
              {/*    {!financingInfo?.financeNum ? (*/}
              {/*      <span className={styles.emptyStateText}>暂无数据</span>*/}
              {/*    ) : (*/}
              {/*      <>*/}
              {/*        <span className={styles.cardInfoValue}>{financingInfo?.financeNum}</span>*/}
              {/*        <span>次</span>*/}
              {/*      </>*/}
              {/*    )}*/}
              {/*  </div>*/}
              {/*</div>*/}
            </div>
            <div className={classnames(styles.dataInfoCard, styles.financingInfoCard)}>
              <div className="">
                <div className="flex-row-wrap flex-start-center">
                  <Dot />
                  <span className={styles.cardInfoName}>母公司累计融资金额</span>
                </div>
                <div className="flex-row-nowrap flex-start-center">
                  {!financingInfo?.totalFinanceAmount ? (
                    <span className={styles.emptyStateText}>暂无数据</span>
                  ) : (
                    <>
                      <span className={styles.cardInfoValue}>{totalFinanceAmount.value}</span>
                      <span>{totalFinanceAmount.unit}</span>
                      {/*<span>万元</span>*/}
                    </>
                  )}
                </div>
              </div>
              {/*<div className="flex-row-nowrap flex-between-center">*/}
              {/*  <div className="flex-row-nowrap flex-between-center">*/}
              {/*    <Dot />*/}
              {/*    <span className={styles.cardInfoName}>融资总金额</span>*/}
              {/*  </div>*/}
              {/*  <div className="flex-row-nowrap flex-between-center">*/}
              {/*    {!financingInfo?.financeAmountNum ? (*/}
              {/*      <span className={styles.emptyStateText}>暂无数据</span>*/}
              {/*    ) : (*/}
              {/*      <>*/}
              {/*        <span className={styles.cardInfoValue}>{financeAmountNum?.value}</span>*/}
              {/*        <span>{financeAmountNum?.unit}</span>*/}
              {/*        /!*<span>万元</span>*!/*/}
              {/*      </>*/}
              {/*    )}*/}
              {/*  </div>*/}
              {/*</div>*/}
            </div>
            <div
              className={classnames(
                'flex-column-wrap flex-center-stretch',
                styles.dataInfoCard,
                styles.financingSeriesCard
              )}
            >
              <div className="">
                <div className="flex-row-wrap flex-start-center">
                  <Dot />
                  <span className={styles.cardInfoName}>母公司当前融资轮次</span>
                </div>
                <div className="flex-row-wrap flex-start-center">
                  {!financingInfo?.lastFinanceRoundsName ? (
                    <span className={styles.emptyStateText}>暂无数据</span>
                  ) : (
                    <>
                      <span className={styles.cardInfoValue}>{financingInfo?.lastFinanceRoundsName}</span>
                      {/* {financingInfo?.lastFinanceRoundsName[financingInfo?.lastFinanceRoundsName.length - 1] !==
                        '轮' && <span>轮次</span>} */}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Chart>
          <Chart
            // title={
            //   <h2 className={styles.chartTitle}>
            //     融资历程表
            //     <ToolTip title="因为部分品牌方不披露金额、或者只披露模糊金额" placement="right" />
            //   </h2>
            // }
            title="母公司融资历程表"
            panelClassName={styles.financingListChart}
          >
            <Table<FinanceForBrandDetailDO>
              className={styles.financingListTable}
              // rowKey="conFinanceRoundsId"
              dataSource={finanncingList}
              columns={financingListColumns}
              pagination={{
                position: ['bottomCenter'],
                pageSize,
                total: finanncingList.length,
                hideOnSinglePage: true,
              }}
            />
          </Chart>
        </div>
        <div className="flex-row-nowrap flex-between-center" style={{ marginTop: 20 }}>
          <Chart
            title={
              <h2 className={styles.chartTitle}>
                行业消费规模
                <TooltipBase
                  title={
                    <>
                      <p>通过行业的消费规模反映行业增长空间与前景，数据已做脱敏处理。</p>
                      <p>数据来源：CBNData数据库</p>
                    </>
                  }
                  placement="right"
                />
              </h2>
            }
            panelClassName={styles.growingSpaceChart}
          >
            {brandLockState !== 'unlocked' ? (
              <LockedCard
                conBrandId={conBrandId}
                backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FonkLY9hWMWE2UkiOHaMpm9RgzSg.png"
                explanation={
                  <>
                    <span>此处为品牌所处行业及赛道的消费规模</span>
                    <span> 若想若想查看该信息，请先开通中国新消费品牌数据库会员</span>
                  </>
                }
                state={brandLockState as LockCardState}
                className={styles.growingSpaceLockedCard}
                explanationClassName={styles.growingSpaceExplanation}
                onStateChange={onStateChange}
              />
            ) : (
              // <div className={styles.growingSpaceChartContent}>
              <div>
                <div
                  className={classnames(
                    'flex-row-nowrap flex-between-center',
                    styles.dataInfoCard,
                    styles.totalScaleCard
                  )}
                >
                  <div className="flex-row-nowrap flex-between-center" style={{ marginRight: 20 }}>
                    <Dot />
                    <span className={styles.cardInfoName}>行业总消费规模指数</span>
                  </div>
                  <div className="flex-row-nowrap flex-between-center">
                    {!financingInfo?.scaleValue ? (
                      <span className={styles.emptyStateText}>暂无数据</span>
                    ) : (
                      <>
                        <span className={classnames(styles.cardInfoValue, 'reddish-orange-text-18')}>
                          {financingInfo?.scaleValue}
                        </span>
                        {/* <span>万元</span> */}
                      </>
                    )}
                  </div>
                </div>
                <LineAndBarMixedChart
                  // lineChartData={potential?.industryGrowSpaceList}
                  barChartData={potential?.industryGrowSpaceList}
                  xAxisName="countTimeStr"
                  // yAxisNameOfLineChart="value"
                  yAxisNameOfBarChart="value"
                  // percentKeyName="growRate"
                  scale={scale}
                  // lineChartGroupBy="dataTypeName"
                  barChartGroupBy="dataTypeName"
                  intervalColor={colors}
                  interverProps={{ size: 80 }}
                  lineColor={colors}
                  legendProps={legendProps}
                  pointProps={{ size: 3 }}
                />
              </div>
            )}
          </Chart>
          <Chart
            title={
              <h2 className={styles.chartTitle}>
                品牌讨论热度
                <TooltipBase
                  title={
                    <>
                      <p>通过小红书平台每月新增的品牌相关笔记点赞数，反映当月该品牌的讨论热度，数据已脱敏处理。</p>
                      <p>数据来源：千瓜数据</p>
                      {/* <p>小红书数据来源：千瓜数据</p> */}
                    </>
                  }
                  placement="right"
                />
              </h2>
            }
            panelClassName={styles.cloutChart}
          >
            {brandLockState !== 'unlocked' ? (
              <LockedCard
                conBrandId={conBrandId}
                backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FqAEOMiR-fx0iWDt1EzowPjCxt6k.png"
                explanation="此处为品牌所属行业与品牌在传统媒体和社交媒体的讨论热度
                若想若想查看该信息，请先开通中国新消费品牌数据库会员"
                state={brandLockState as LockCardState}
                className={styles.discussionCloutLockedCard}
                explanationClassName={styles.discussionCloutExplanation}
                onStateChange={onStateChange}
              />
            ) : potential?.hotTalkEmpty ? (
              <CustomEmpty className={styles.emptyState} />
            ) : (
              <LineAndBarMixedChart
                height={340}
                // lineChartData={potential?.hotTalkList}
                barChartData={potential?.hotTalkList}
                xAxisName="countTimeStr"
                // yAxisNameOfLineChart="value"
                yAxisNameOfBarChart="value"
                // percentKeyName="growRate"
                scale={scale}
                // lineChartGroupBy="dataTypeName"
                barChartGroupBy="dataTypeName"
                // lineColor={colors}
                intervalColor={colors}
                legendProps={cloutLegendProps}
                pointProps={{ size: 3 }}
              />
            )}
          </Chart>
        </div>
      </>
    )
  );
};

export default RacetrackAndBrandPotential;
